import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Image,
  Link,
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
} from '@heroui/react';
import React, { useCallback, useMemo } from 'react';
import { IoChevronDownOutline, IoMenuOutline } from 'react-icons/io5';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import logoUrl from '/content/img/logo-color-notext.png';

interface MenuItem {
  title: string;
  href?: string;
  inTitleBar?: boolean;
  children?: MenuItem[];
  showDivider?: boolean;
}

const menuItems: MenuItem[] = [
  {
    title: 'News',
    href: '/',
    inTitleBar: true,
  },
  {
    title: 'About',
    href: '/about',
    inTitleBar: true,
  },
  {
    title: 'Contribute',
    href: '/contribute',
    inTitleBar: true,
    showDivider: true,
  },
  {
    title: 'Projects',
    inTitleBar: true,
    showDivider: true,
    children: [
      //   {
      //     title: 'Alleyway Cleanup',
      //     href: '/projects/alleyway-cleanup',
      //   },
      {
        title: 'Annual Block Party',
        href: '/projects/block-party',
      },
      {
        title: 'Desert Garden Median',
        href: '/projects/desert-garden-median',
      },
      {
        title: 'Historical Status',
        href: '/projects/historical-status',
      },
      {
        title: 'Road Repaving',
        href: '/projects/repaving',
      },
      {
        title: 'Speed Mitigation on N Camino Seco',
        href: '/projects/traffic',
      },
    ],
  },
  {
    title: 'Board of Directors',
    href: '/board',
    inTitleBar: false,
  },
];

export function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const navigate = useNavigate();

  const menuBarItems = useMemo(() => {
    return menuItems.filter((item) => item.inTitleBar);
  }, []);

  const followLink = useCallback(
    (key: string) => {
      for (const item of menuItems) {
        if (item.title === key) {
          if (item.href) {
            navigate(item.href);
            return;
          }
        }

        for (const child of item.children || []) {
          if (child.title === key) {
            navigate(child.href);
            return;
          }
        }
      }
    },
    [navigate],
  );

  const menuBar = useMemo(() => {
    return menuBarItems.map((item) => (
      <NavbarItem key={item.title} onClick={() => setIsMenuOpen(false)}>
        {item.children?.length > 0 ? (
          <Dropdown triggerType="menu" showArrow>
            <DropdownTrigger className="font-light text-medium cursor-pointer">
              <div className="flex gap-0.5 items-center">
                {item.title}
                <IoChevronDownOutline className="mt-0.5" size={14} />
              </div>
            </DropdownTrigger>
            <DropdownMenu title={item.title} onAction={(key) => followLink(key)}>
              {item.children.map((child) => (
                <DropdownItem key={child.title}>{child.title}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        ) : (
          <RouterLink color="foreground" to={item.href}>
            {item.title}
          </RouterLink>
        )}
      </NavbarItem>
    ));
  }, [menuBarItems.map, followLink]);

  return (
    <Navbar
      isBordered
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
      maxWidth="lg"
      className="flex"
      height="5rem"
      isBlurred={true}
    >
      <NavbarBrand>
        <RouterLink to="/">
          <Image src={logoUrl} alt="WPC Logo" width="100" height="60" radius="none" />
        </RouterLink>
      </NavbarBrand>

      <NavbarContent className="hidden sm:flex gap-6" justify="center">
        {menuBar}
      </NavbarContent>

      <NavbarContent className="sm:hidden" justify="end">
        <Button isIconOnly variant="light" size="lg" onPress={() => setIsMenuOpen(!isMenuOpen)}>
          <IoMenuOutline size={24} />
        </Button>
      </NavbarContent>

      <NavbarContent className="hidden sm:flex" justify="end">
        <Dropdown placement="bottom-end" showArrow>
          <DropdownTrigger>
            <Button isIconOnly variant="light" size="lg">
              <IoMenuOutline size={24} />
            </Button>
          </DropdownTrigger>
          <DropdownMenu title="Menu" onAction={(key) => followLink(key)}>
            {menuItems.map((item) => {
              if (item.children?.length > 0) {
                return (
                  <DropdownSection showDivider={item.showDivider} key={item.title} title={item.title}>
                    {item.children.map((child) => (
                      <DropdownItem key={child.title}>{child.title}</DropdownItem>
                    ))}
                  </DropdownSection>
                );
              } else {
                return (
                  <DropdownItem showDivider={item.showDivider} key={item.title}>
                    {item.title}
                  </DropdownItem>
                );
              }
            })}
          </DropdownMenu>
        </Dropdown>
      </NavbarContent>

      <NavbarMenu>
        {menuItems.map((item, idx) => (
          <NavbarMenuItem key={item.title}>
            <Link
              key={item.title}
              className="w-full mb-1"
              href={item.href}
              color="foreground"
              size="lg"
              onPress={() => !item.children && setIsMenuOpen(false)}
            >
              {item.title}
            </Link>
            {item.children?.map((child) => (
              <Link
                key={child.title}
                className="w-full ml-6 mb-1"
                href={child.href}
                color="foreground"
                size="lg"
                onPress={() => setIsMenuOpen(false)}
              >
                {child.title}
              </Link>
            ))}
            {idx !== menuItems.length - 1 && <Divider className="mt-1" />}
          </NavbarMenuItem>
        ))}
      </NavbarMenu>
    </Navbar>
  );
}
